import { NON_CONVERSATION_TRIGGERS, type WorkflowTrigger } from "@sagot/constants";
import { talksToVisitor } from "./definition-model";
import { exitSlotsOf } from "./exit-slots";
import type { Problem } from "./problem-model";
import { TICKET_TRIGGERS } from "./service/service-facts";
import { isServiceStep, isTicketFacingStep } from "./service/service-model";
import type { Definition, Step } from "./types";

/**
 * The rules of the backend's `graph_service.py` (pure): the Service Desk steps against the trigger and the kind of workflow.
 *
 * - A step that acts on the run's ticket needs one: a ticket trigger (ST1-ST17), or a Create ticket step in the same
 *   workflow (it names the new ticket for the steps after it). A conversation trigger without either has no ticket.
 * - Link ticket links the run's conversation, so the trigger must have one; linking by "the event's ticket" needs an event
 *   that names both, which none does in this release (link by a variable).
 * - An exit sends a background run down another path. A workflow that talks to the visitor runs these steps in the visitor's
 *   turn and carries on with the next step, so an exit there would do nothing: it is refused.
 */

/** Steps that act on the run's ticket (backend `TICKET_ACTING_STEPS`). */
const TICKET_ACTING_STEPS: ReadonlySet<string> = new Set([
  "update_ticket",
  "set_ticket_state",
  "route_ticket",
  "assign_ticket",
  "add_ticket_note",
  "reply_to_ticket",
  "notify_contact",
  "apply_ticket_sla",
  "request_csat",
]);

/** Whether an exit of the step is connected to a path. */
const hasConnectedExit = (step: Step): boolean => exitSlotsOf(step).some((slot) => slot.target !== undefined);

interface Located {
  path: string;
  step: Step;
}

const located = (definition: Definition): Located[] => Object.entries(definition.paths).flatMap(([path, body]) => body.steps.map((step) => ({ path, step })));

const ofStep = (code: string, at: Located, params: Problem["params"] = {}): Problem => ({ code, where: `step ${at.step.id}`, path: at.path, stepId: at.step.id, params, source: "client" });

export function serviceProblems(definition: Definition, trigger: WorkflowTrigger): Problem[] {
  const steps = located(definition);
  const makesTicket = steps.some((at) => at.step.type === "create_ticket");
  const talks = steps.some((at) => talksToVisitor(at.step));
  const problems: Problem[] = [];
  for (const at of steps) {
    const { step } = at;
    if (TICKET_ACTING_STEPS.has(step.type) && !TICKET_TRIGGERS.has(trigger) && !makesTicket) problems.push(ofStep("no_ticket_here", at));
    if (step.type === "link_ticket") {
      if (NON_CONVERSATION_TRIGGERS.has(trigger) || TICKET_TRIGGERS.has(trigger)) problems.push(ofStep("link_needs_conversation", at));
      else if (step.source === "event") problems.push(ofStep("link_event_no_ticket", at));
    }
    // The three ticket steps the client sees are meant to have exits; only the background ones are refused in a talking workflow.
    if (talks && isServiceStep(step) && !isTicketFacingStep(step) && hasConnectedExit(step)) {
      problems.push(ofStep("service_exits_not_here", at, { step: step.type }));
    }
  }
  return problems;
}
